import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { V3_APP_BASE_URL } from '@/constants/Config';
import type { IDesignCard } from '@/common/interfaces/design.interface';
import { removeDuplicatesWithFromTop } from '@/utilities/CommonUtils';

interface IDesignSectionResp {
  proofs: {
    rows: IDesignCard[];
    count: number;
  };
}

interface IErrRes {
  status: string | number;
  data: { message: string };
}

/** Defining a service using a base URL and expected endpoints  */
export const designsApi = createApi({
  reducerPath: 'designsApi',
  tagTypes: ['Revision'],
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (build) => ({
    getDesigns: build.query({
      query: ({ limit, page, fetchOnlyDone }) => ({
        url: `api/cp/proof${fetchOnlyDone ? '/true' : ''}?limit=${limit}&page=${page}`,
        method: 'get',
      }),
      transformResponse: (response: IDesignSectionResp) => ({
        proofs: response.proofs.rows,
        count: response.proofs.count,
      }),
      providesTags: ['Revision'],
      /** Only have one cache entry because the arg always maps to one string */
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.proofs.push(...newItems.proofs);
        currentCache.proofs = removeDuplicatesWithFromTop(currentCache.proofs, 'id');
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getDesign: build.query({
      query: ({ id }) => ({ url: `api/cp/proof-details/${id}`, method: 'get' }),
      providesTags: ['Revision'],
    }),
    sendOrderWithSalesRepEmail: build.mutation({
      query: ({ id }) => ({
        url: `api/cp/order/send-order-create-email/${id}`,
        method: 'GET',
      }),
      transformResponse: () => ({
        message: 'Email sent successfully.',
        status: 'success',
      }),
      transformErrorResponse: (response: IErrRes) => ({
        message: response.data.message,
        status: 'error',
      }),
    }),
    getDesignItem: build.query({
      query: ({ proofId, proofItemId }) => ({
        url: `api/cp/proof-item-details/${proofId}/${proofItemId}`,
        method: 'get',
      }),
      providesTags: ['Revision'],
    }),
    createRevision: build.mutation({
      query: ({ data }) => ({
        url: `api/cp/proof-item/true`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Revision'],
      transformResponse: () => ({
        message: 'Successfully placed a revision request.',
        status: 'success',
      }),
      transformErrorResponse: () => ({
        message: 'Failed to place a revision request.',
        status: 'error',
      }),
    }),
    getDoneDesigns: build.query({
      query: ({ limit, page, fetchOnlyDone }) => ({
        url: `api/cp/proofs/done${fetchOnlyDone ? '/true' : ''}?limit=${limit}&page=${page}`,
        method: 'get',
      }),
      transformResponse: (response: IDesignSectionResp) => ({
        proofs: response.proofs.rows,
        count: response.proofs.count,
      }),
      providesTags: ['Revision'],
      /** Only have one cache entry because the arg always maps to one string */
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.proofs.push(...newItems.proofs);
        currentCache.proofs = removeDuplicatesWithFromTop(currentCache.proofs, 'id');
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    sendStoreWithSalesRepEmail: build.mutation({
      query: ({ id }) => ({
        url: `api/cp/order/send-store-create-email/${id}`,
        method: 'GET',
      }),
      transformResponse: () => ({
        message: 'Your Store Request Is In.',
        status: 'success',
      }),
      transformErrorResponse: (response: IErrRes) => ({
        message: response.data.message,
        status: 'error',
      }),
    }),
    updateDesignPricing: build.mutation({
      query: ({ id, data }) => ({
        url: `api/v4/design-pricing/${id}`,
        method: 'PUT',
        data: { ...data },
      }),
      /**
       * This function handles the query start logic.
       *
       * @param {Object} arg - The arguments passed to the function.
       * @param {boolean} arg.shouldInvalidate - Determines whether to invalidate the tags.
       * @param {Object} tools - The tools provided by the query (dispatch, queryFulfilled).
       * @param {Function} tools.dispatch - Redux dispatch function.
       * @param {Promise} tools.queryFulfilled - Promise that fulfills when the query is finished.
       *
       * @returns {Promise<void>} - A promise that resolves when the operation is complete.
       */
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          if (arg.shouldInvalidate) {
            dispatch(designsApi.util.invalidateTags([{ type: 'Revision' }]));
          }
        } catch {
          // Handle error if necessary
        }
      },
      transformResponse: () => ({
        message: 'Successfully placed a design pricing update.',
        status: 'success',
      }),
      transformErrorResponse: () => ({
        message: 'Failed to place a update design pricing.',
        status: 'error',
      }),
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const {
  useGetDesignsQuery,
  useGetDesignQuery,
  useSendOrderWithSalesRepEmailMutation,
  useGetDesignItemQuery,
  useCreateRevisionMutation,
  useGetDoneDesignsQuery,
  useLazyGetDesignItemQuery,
  useSendStoreWithSalesRepEmailMutation,
  useUpdateDesignPricingMutation,
} = designsApi;
